'use strict';
import isFunction from 'lodash/isFunction';
import AccCore from 'opentok-accelerator-core';
import {createEventHandler, mapPropsStream} from 'recompose';
import {Observable} from 'rxjs';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/operator/withLatestFrom';
import {isWebBuild} from '../../../config/constants';
import {createToggleState} from '../../../core-utilities/react-utils';
import {VideoChatSessionView} from './VideoChatSessionView';


const START = 'start';
const END = 'end';

export const StreamedComponent = (props$) => {
  const {
    handler: onClickBack,
    stream: clickBack$,
  } = createEventHandler();
  const {
    handler: onClickStartCall,
    stream: startCall$,
  } = createEventHandler();
  const {
    handler: onClickEndCall,
    stream: endCall$,
  } = createEventHandler();
  const {
    handler: onToggleCamera,
    stream: selectedCamera$,
  } = createEventHandler();
  const {
    handler: onToggleLocalAudio,
    stream: isLocalAudioEnabled$,
  } = createToggleState(props$, true);
  const {
    handler: onToggleLocalVideo,
    stream: isLocalVideoEnabled$,
  } = createToggleState(props$, true);
  const {
    handler: onToggleChat,
    stream: isChatDisplayed$,
  } = createToggleState(props$);

  clickBack$
    .withLatestFrom(props$)
    .takeUntil(props$.last())
    .subscribe(([_, props]) => {
      if (isFunction(props.onEndCall)) {
        props.onEndCall();
      }
    });

  const otCore$ = props$
    .filter(props => !!props.otCoreOptions)
    .take(1)
    .map(props => new AccCore(props.otCoreOptions))
    .takeUntil(props$.last())
    .share();

  const state$ = otCore$
    .switchMap(otCore =>
      Observable
        .merge(
          buildCallHandlers({
            props$,
            otCore,
            startCall$,
            endCall$,
            isLocalAudioEnabled$,
            isLocalVideoEnabled$,
          }),
          buildEvents(otCore),
          buildConnect(otCore),
        )
    )
    .startWith({
      connected: false,
      joining: false,
      active: false,
      publishers: null,
      subscribers: null,
      meta: null,
    })
    .scan((acc, curr) => ({
      ...acc,
      ...curr,
    }))
    .takeUntil(props$.last())
    .share();

  props$
    .last()
    .take(1)
    .withLatestFrom(otCore$, state$)
    .subscribe(([props, otCore, state]) => {
      if (otCore && state.connected) {
        otCore.disconnect();
      }
    });

  selectedCamera$
    .withLatestFrom(isLocalVideoEnabled$, otCore$)
    .subscribe(([_, isLocalVideoEnabled, otCore]) => {
      if (isLocalVideoEnabled && otCore) {
        const publishers = Object.values(otCore.state().publishers.camera);
        if (publishers[0]) {
          try {
            const result = publishers[0].cycleVideo();

            if (result && result.catch)
              result.catch(e => console.error('failed to change camera (async)', e));
          } catch (e) {
            console.error('failed to change camera (sync)', e);
          }
        }
      }
    });

  isLocalAudioEnabled$
    .withLatestFrom(otCore$)
    .subscribe(([isLocalAudioEnabled, otCore]) => otCore && otCore.toggleLocalAudio(isLocalAudioEnabled));

  isLocalVideoEnabled$
    .withLatestFrom(otCore$)
    .subscribe(([isLocalVideoEnabled, otCore]) => otCore && otCore.toggleLocalVideo(isLocalVideoEnabled));

  return props$
    .combineLatest(
      state$,
      isLocalAudioEnabled$,
      isLocalVideoEnabled$,
      isChatDisplayed$,
      (props, state, isLocalAudioEnabled, isLocalVideoEnabled, isChatDisplayed) => ({
        ...props,
        ...state,
        supportsChat: isWebBuild(),
        isLocalAudioEnabled,
        isLocalVideoEnabled,
        isChatDisplayed,
        onClickBack,
        onClickStartCall,
        onClickEndCall,
        onToggleCamera,
        onToggleLocalAudio,
        onToggleLocalVideo,
        onToggleChat,
      }))
    .do(props => console.log('next VideoChatSessionComponent2.js props', props));
};

export const VideoChatSessionComponent = mapPropsStream(StreamedComponent)(VideoChatSessionView);
VideoChatSessionComponent.displayName = 'VideoChatSessionComponent';

function buildEvents(otCore) {
  const events = [
    'subscribeToCamera',
    'unsubscribeFromCamera',
    'subscribeToScreen',
    'unsubscribeFromScreen',
    'startScreenShare',
    'endScreenShare',
  ];

  const streams$ = events
    .map(event => wrapEvent(otCore, event));

  return Observable
    .merge(...streams$)
    .map(({publishers, subscribers, meta}) => {
      console.info(`this.otCore.on(${event})`);

      // Filter the output
      return {publishers, subscribers, meta};
    })
    .merge()
    .catch(err => {
      console.error('buildEvents', err);
      return Observable.empty();
    });
}

// TODO: handle error case
function buildConnect(otCore) {
  return Observable.fromPromise(otCore.connect())
    .map(() => ({connected: true}))
    .catch(err => {
      console.error('buildConnect', err);
      return Observable.of({err});
    });
}

function buildCallHandlers(config) {
  const {
    props$,
    otCore,
    startCall$,
    endCall$,
    isLocalAudioEnabled$,
    isLocalVideoEnabled$,
  } = config;

  return Observable
    .merge(
      startCall$.mapTo(START),
      endCall$.mapTo(END),
    )
    .withLatestFrom(props$, isLocalAudioEnabled$, isLocalVideoEnabled$)
    .mergeMap(([action, props, isLocalAudioEnabled, isLocalVideoEnabled]) => {
      if (action === START) {
        console.info('startCall initiating');
        try {
          return Observable
            .merge(
              Observable.of({joining: true}),
              Observable.fromPromise(otCore.startCall({
                publishAudio: isLocalAudioEnabled,
                publishVideo: isLocalVideoEnabled,
              }))
                .map(({publishers, subscribers, meta}) => ({
                  publishers,
                  subscribers,
                  meta,
                  active: true,
                  joining: false,
                }))
                .do(data => console.info('startCall success', data))
                .catch(err => Observable.of({active: false, joining: false, err}))
            );
        } catch (err) {
          console.error('startCall failed', err);
          return Observable.of({active: false, joining: false, err});
        }

      } else if (action === END) {
        otCore.endCall();
        otCore.disconnect();

        if (isFunction(props.onEndCall)) {
          props.onEndCall();
        }

        return Observable.of({active: false, connected: false});

      } else {
        return Observable.empty();
      }
    });
}

function wrapEvent(otCore, event) {
  return Observable
    .fromEventPattern(
      function add(h) {
        otCore.on(event, h);
      },
      function remove(h) {
        otCore.off(event, h);
      },
    );
}



// WEBPACK FOOTER //
// ./src/js/comments/components/VideoChatSession/VideoChatSessionComponent2.js